/**
 * Structured view of a command run: headline tiles, a paginated table, and the
 * handoff button that loads one command's findings into the next command's
 * arguments.
 *
 * Raw stdout is still available behind a toggle — it is the thing an operator
 * would paste into a ticket — but it is no longer the primary way to read a
 * result. A 176-row finding is a table, not a wall of monospace.
 */
import { useMemo, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, Terminal } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import type { CommandResult, CommandResultHandoff } from '../../../lib/api';

const PAGE_SIZE = 25;

const TONE_CLASS: Record<string, string> = {
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  warn: 'border-amber-200 bg-amber-50 text-amber-900',
  bad: 'border-red-200 bg-red-50 text-red-900',
};

interface CommandRunResultProps {
  result: CommandResult;
  stdout: string;
  onHandoff?: (handoff: CommandResultHandoff) => void;
}

export function CommandRunResult({ result, stdout, onHandoff }: CommandRunResultProps) {
  const [page, setPage] = useState(0);
  const [showRaw, setShowRaw] = useState(false);

  const table = result?.table;
  const rows = useMemo(() => table?.rows ?? [], [table]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [rows, page],
  );

  const hasStructured = Boolean(result?.summary?.length || table?.rows?.length);

  // Nothing structured to show: the raw report IS the result.
  if (!hasStructured) {
    return stdout ? (
      <pre className="max-h-96 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100 whitespace-pre-wrap">
        {stdout}
      </pre>
    ) : null;
  }

  return (
    <div className="space-y-4">
      {result.summary && result.summary.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {result.summary.map((tile, i) => (
            <div
              key={`${tile.label}-${i}`}
              className={`rounded-md border px-3 py-2 ${
                TONE_CLASS[tile.tone ?? ''] ?? 'border-slate-200 bg-slate-50 text-slate-900'
              }`}
            >
              <div className="text-[11px] uppercase tracking-wide opacity-70">
                {tile.label}
              </div>
              <div className="text-lg font-semibold tabular-nums">{tile.value}</div>
            </div>
          ))}
        </div>
      )}

      {result.handoff && onHandoff && (
        <div className="flex items-center gap-3 rounded-md border border-blue-200 bg-blue-50 p-3">
          <Button size="sm" onClick={() => onHandoff(result.handoff!)}>
            {result.handoff.label}
            <ArrowRight className="ml-2 h-3 w-3" />
          </Button>
          <span className="text-xs text-blue-900">
            Loads these {result.handoff.values.length.toLocaleString()} id(s)
            straight into the next command — no copying.
          </span>
        </div>
      )}

      {table && rows.length > 0 && (
        <div className="rounded-md border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {table.columns.map((col) => (
                    <th
                      key={col.key}
                      className={`px-3 py-2 font-medium text-slate-600 whitespace-nowrap ${
                        col.align === 'right' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                    {table.columns.map((col) => {
                      const value = row[col.key];
                      if (col.key === 'flag') {
                        return (
                          <td key={col.key} className="px-3 py-1.5">
                            {value ? (
                              <Badge variant="secondary"
                                     className="bg-red-100 text-red-700 font-medium">
                                {value}
                              </Badge>
                            ) : null}
                          </td>
                        );
                      }
                      return (
                        <td
                          key={col.key}
                          className={`px-3 py-1.5 whitespace-nowrap ${
                            col.align === 'right' ? 'text-right tabular-nums' : ''
                          }`}
                        >
                          {value === null || value === undefined ? '—' : value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2">
            <span className="text-xs text-muted-foreground">
              {rows.length.toLocaleString()} row{rows.length === 1 ? '' : 's'}
              {table.total > rows.length && ` of ${table.total.toLocaleString()}`}
              {pageCount > 1 && ` · page ${page + 1} of ${pageCount}`}
            </span>
            {pageCount > 1 && (
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= pageCount - 1}
                        onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {stdout && (
        <div>
          <Button variant="ghost" size="sm" onClick={() => setShowRaw((v) => !v)}>
            <Terminal className="mr-2 h-3 w-3" />
            {showRaw ? 'Hide' : 'Show'} raw output
          </Button>
          {showRaw && (
            <pre className="mt-2 max-h-96 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100 whitespace-pre-wrap">
              {stdout}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
